'use client';

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { toDisplayImageUrl } from '@/lib/image-proxy/toDisplayImageUrl';

/**
 * Which paid-tier advertiser-attribution treatment to render. Mirrors the two
 * escalated `@loci/ui` `SponsoredTier` values that DES-50 gives an advertiser
 * brand slot to — Starter is intentionally excluded (pill only, no logo, no
 * attribution line).
 */
export type SponsoredAttributionTier = 'growth' | 'featured';

export interface SponsoredAttributionProps {
  /**
   * Paid tier. Drives the copy (`Sponsored by` vs `Presented by`) and the
   * container treatment (neutral footer band vs amber header band) per DES-50.
   */
  tier: SponsoredAttributionTier;
  /**
   * Advertiser display name (`SponsoredEventMeta.advertiserName`) — always
   * present on the wire (API-1612). Rendered as the bold subject of the
   * attribution line.
   */
  advertiserName: string;
  /**
   * Advertiser brand logo (`SponsoredEventMeta.logoUrl`) or `null`. HONEST
   * ABSENCE (WEB-537 / API-1612): `null`, any URL the CSP cannot render
   * (`toDisplayImageUrl` collapses an un-proxiable host to `''`), AND any logo
   * that fails to LOAD (proxied 404 / decode failure / CSP block — see
   * `onError` below) all render the name/border treatment with NO `<img>`,
   * never a placeholder or broken image. This deliberately supersedes the
   * DES-50 mockup's monogram-fallback square.
   *
   * NOTE: `toDisplayImageUrl` only yields a renderable URL for CSP-safe origins
   * (own CDN, Vercel Blob, allow-listed upstreams); an advertiser logo on an
   * arbitrary admin-entered host collapses to honest-absence. Rendering the
   * paid brand logo for that common case requires the admin capture flow to
   * host logos on a CSP-safe origin (e.g. Vercel Blob) — tracked as a
   * follow-up, out of this card-rendering issue's scope.
   */
  logoUrl: string | null;
  /** Additional classes merged onto the attribution band. */
  className?: string;
}

const PREFIX_COPY: Record<SponsoredAttributionTier, string> = {
  growth: 'Sponsored by',
  featured: 'Presented by',
};

/**
 * SponsoredAttribution — the Growth-tier "Sponsored by {advertiser}" footer and
 * the Featured+-tier "Presented by {advertiser}" header band (DES-50 tier
 * escalation, WEB-537).
 *
 * DES-50 renders these on a poster card (logo left of an attribution line);
 * `DiscoverEventCard` is a list row, so it places this band as the last child of
 * the body column (Growth footer, `border-top`) or as a top band under the
 * disclosure pill (Featured+ header, amber tint + `border-bottom`) — the same
 * poster-card → list-row adaptation WEB-36/WEB-535 already made for the pill.
 *
 * Pure presentational; the 24px logo is a plain `<img>` routed through the
 * same-origin CSP image proxy (`toDisplayImageUrl`) exactly like `EventRailCard`
 * media, because advertiser logos are arbitrary admin-entered hosts that
 * `next/image` `remotePatterns` cannot enumerate. `alt=""` (decorative): the
 * adjacent text already names the advertiser, so screen readers don't double up.
 */
export function SponsoredAttribution({
  tier,
  advertiserName,
  logoUrl,
  className,
}: SponsoredAttributionProps): React.ReactElement {
  const displayLogoUrl = toDisplayImageUrl(logoUrl);
  // Extend honest-absence to LOAD failures: a CSP-safe URL that 404s / fails to
  // decode must collapse to no-image too, so a paid card never shows a broken
  // logo. Reset when the URL changes (the discover feed recreates event objects
  // on every fetch, so the same mounted band can receive a new advertiser).
  const [logoFailed, setLogoFailed] = React.useState(false);
  React.useEffect(() => {
    setLogoFailed(false);
  }, [displayLogoUrl]);
  const showLogo = displayLogoUrl.length > 0 && !logoFailed;
  const isFeatured = tier === 'featured';

  return (
    <div
      data-testid="sponsored-attribution"
      data-tier={tier}
      data-has-logo={showLogo ? 'true' : 'false'}
      className={cn(
        'flex min-w-0 items-center gap-2',
        isFeatured
          ? // Featured+ header band — amber (`--cta`, theme-aware) tint + bottom
            // rule, 9/14px padding per the DES-50 "presented-by bar" redline.
            'border-b border-[color-mix(in_srgb,var(--cta)_35%,transparent)] bg-[color-mix(in_srgb,var(--cta)_10%,transparent)] px-3.5 py-[9px]'
          : // Growth footer band — neutral top rule, 10/16px padding per the
            // DES-50 "footer" redline.
            'border-t border-loci-border px-4 py-2.5',
        className,
      )}
    >
      {showLogo && (
        <img
          src={displayLogoUrl}
          alt=""
          width={24}
          height={24}
          loading="lazy"
          decoding="async"
          onError={() => setLogoFailed(true)}
          data-testid="sponsored-attribution-logo"
          className="h-6 w-6 shrink-0 rounded-ds-sm object-cover"
        />
      )}
      <span className="min-w-0 truncate text-caption leading-tight text-loci-text-muted">
        {PREFIX_COPY[tier]} <b className="font-semibold text-loci-text">{advertiserName}</b>
      </span>
    </div>
  );
}
